import React from 'react';

import useTheme from '@hero/branding/theme';
import CheckboxSquare from '@hero/ui-kit/graphics/CheckboxSquare';
import Container from '@hero/ui-kit/layout/Container';
import P from '@hero/ui-kit/typography/P';
import { useLocation, useNavigate } from 'react-router-dom';
import HighlightedText from '../../../components/HighlightedText';
import SelectableText from '../../../components/SelectableText';
import { getBillingFormattedDate } from '../../../utils/date';
import {
    CaregiverInfo,
    CmMonthlyCheckin,
    ExchangeDeviceInfo,
    MemberRole,
    MemberStatus,
    OrganizationMemberListDeviceInfo
} from '../types';
import { GetOrganizationMembersParams } from '../api/useGetOrganizationMembers';
import { writeMemberListSnapshot } from '../OrganizationMemberDetails/MemberDetailsWrapper/useMemberListNeighbors';
import getMemberUrl from '../utils/getMemberUrl';
import getMemberRoleLabel from './utils/getMemberRoleLabel';
import getMembershipLabel from './utils/getMembershipLabel';
import Caregivers from './Caregivers';
import DeviceSerial from './DeviceSerial';
import DeviceStatus, { DeviceAlert } from './DeviceStatus';
import CareManagerBadge from '../components/CareManagerBadge';
import CheckinStatusPill from '../components/CheckinStatusPill';
import { getDeviceMismatch } from '../utils/deviceProfileMismatch';
import { MemberColumnId } from './columns';
import * as Style from './style.module.scss';

const ListItem: React.FC<{
    memberId?: number | null;
    heroUserId?: number | null;
    activatedAt?: string | null;
    externalUserId?: string | null;
    name?: string;
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    deviceFirstName?: string | null;
    deviceLastName?: string | null;
    deviceDateOfBirth?: string | null;
    email?: string;
    deviceInfo?: OrganizationMemberListDeviceInfo;
    exchangeDeviceInfo?: ExchangeDeviceInfo;
    careManagerIds: number[] | null;
    caregivers?: CaregiverInfo[];
    cmMonthlyCheckin?: CmMonthlyCheckin | null;
    search: string;
    role: MemberRole | null;
    memberStatus?: MemberStatus | null;
    canOpenDetails?: boolean;
    isBulkMode?: boolean;
    isSelected?: boolean;
    onToggleSelect?: (memberId: number) => void;
    listQuery: GetOrganizationMembersParams;
    isColumnVisible: (id: MemberColumnId) => boolean;
    gridColumns: string;
}> = ({
    name,
    firstName,
    lastName,
    dateOfBirth,
    deviceFirstName,
    deviceLastName,
    deviceDateOfBirth,
    heroUserId,
    memberId,
    email,
    deviceInfo,
    careManagerIds,
    caregivers,
    cmMonthlyCheckin,
    search,
    exchangeDeviceInfo,
    externalUserId,
    activatedAt,
    role,
    memberStatus,
    canOpenDetails = false,
    isBulkMode = false,
    isSelected = false,
    onToggleSelect,
    listQuery,
    isColumnVisible,
    gridColumns
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const hasId: boolean = React.useMemo(() => {
        return !!memberId || !!heroUserId;
    }, [memberId, heroUserId]);

    const isSelectable = isBulkMode && !!memberId;

    // A completed check-in is dated by when it happened; anything still open by when it is due.
    const checkinDate = cmMonthlyCheckin?.checkin_date || cmMonthlyCheckin?.scheduled_date;

    const { hasMismatch, content: mismatchContent } = getDeviceMismatch({
        firstName,
        lastName,
        dateOfBirth,
        deviceFirstName,
        deviceLastName,
        deviceDateOfBirth
    });

    const handleClick = React.useCallback(
        (e: React.SyntheticEvent) => {
            if (isBulkMode) {
                if (isSelectable && onToggleSelect && memberId) {
                    onToggleSelect(memberId);
                }
                return;
            }
            if (!e.isDefaultPrevented() && hasId && canOpenDetails) {
                const from = `${location.pathname}${location.search}`;
                const snapshot = { from, listQuery, listKind: 'members' as const };
                writeMemberListSnapshot(snapshot);
                navigate(`${getMemberUrl(heroUserId, memberId)}`, { state: snapshot });
            }
        },
        [
            isBulkMode,
            isSelectable,
            onToggleSelect,
            memberId,
            hasId,
            heroUserId,
            navigate,
            canOpenDetails,
            location,
            listQuery
        ]
    );

    return (
        <div
            onClick={handleClick}
            onKeyDown={() => null}
            tabIndex={0}
            role="button"
            style={{
                cursor: isBulkMode
                    ? isSelectable
                        ? 'pointer'
                        : 'not-allowed'
                    : hasId
                      ? 'pointer'
                      : 'not-allowed',
                opacity: isBulkMode && !isSelectable ? 0.5 : 1
            }}
            className={Style.memberListWrapper}
        >
            <Container
                gridTemplateColumns={gridColumns}
                gridColumnGap="small"
                className={Style.container}
            >
                <>
                    {isBulkMode && (
                        <div className={Style.checkboxCell}>
                            <CheckboxSquare
                                isChecked={isSelected}
                                isHovered={false}
                                isFocused={false}
                                disabled={!isSelectable}
                                styles={{
                                    width: '2rem',
                                    height: '2rem',
                                    minWidth: '2rem',
                                    cursor: 'inherit'
                                }}
                            />
                        </div>
                    )}

                    <div className={Style.memberCell}>
                        <P noDefaultMargin strong>
                            <HighlightedText highlight={search} text={name} />
                        </P>
                        {email && (
                            <>
                                <P
                                    size="small"
                                    noDefaultMargin
                                    className={Style.truncate}
                                    title={email}
                                    styles={{
                                        color: theme.colors.neutrals.textGamma
                                    }}
                                >
                                    <HighlightedText highlight={search} text={email} />
                                </P>
                                {externalUserId && !isColumnVisible('patientId') && (
                                    <P
                                        size="small"
                                        strong
                                        noDefaultMargin
                                        className={Style.truncate}
                                        title={externalUserId}
                                        styles={{
                                            color: theme.colors.neutrals.textGamma
                                        }}
                                    >
                                        <HighlightedText highlight={search} text={externalUserId} />
                                    </P>
                                )}
                            </>
                        )}
                    </div>

                    {isColumnVisible('patientId') && (
                        <div className={`flex-center-vertical ${Style.truncate}`}>
                            <P
                                noDefaultMargin
                                className={Style.truncate}
                                title={externalUserId || undefined}
                            >
                                <HighlightedText
                                    highlight={search}
                                    text={externalUserId || 'N/A'}
                                />
                            </P>
                        </div>
                    )}

                    {isColumnVisible('device') && (
                        <div className="flex-center-vertical">
                            <SelectableText
                                style={{ padding: '1rem 1rem 1rem 1.5rem', marginLeft: '-1.5rem' }}
                            >
                                <DeviceSerial
                                    deviceInfo={deviceInfo}
                                    search={search}
                                    exchangeDeviceInfo={exchangeDeviceInfo}
                                />
                            </SelectableText>
                        </div>
                    )}

                    {isColumnVisible('memberSince') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                {activatedAt
                                    ? getBillingFormattedDate(activatedAt, 'MMM d, yyyy')
                                    : 'Not Activated Yet'}
                            </P>
                        </div>
                    )}

                    {isColumnVisible('careManager') && (
                        <div className="flex-center-vertical">
                            <CareManagerBadge careManagerIds={careManagerIds} />
                        </div>
                    )}

                    {isColumnVisible('caregivers') && (
                        <Caregivers search={search} caregivers={caregivers} />
                    )}
                    {isColumnVisible('status') && (
                        <DeviceStatus
                            deviceInfo={deviceInfo}
                            exchangeDeviceInfo={exchangeDeviceInfo}
                        />
                    )}
                    {isColumnVisible('checkin') && (
                        <div className={Style.checkinCell}>
                            {cmMonthlyCheckin ? (
                                <>
                                    <CheckinStatusPill status={cmMonthlyCheckin.status} />
                                    {checkinDate ? (
                                        <P
                                            size="small"
                                            noDefaultMargin
                                            styles={{ color: theme.colors.neutrals.textGamma }}
                                        >
                                            {getBillingFormattedDate(checkinDate, 'MMM d, yyyy')}
                                        </P>
                                    ) : null}
                                </>
                            ) : (
                                <P
                                    noDefaultMargin
                                    styles={{ color: theme.colors.neutrals.textGamma }}
                                >
                                    &mdash;
                                </P>
                            )}
                        </div>
                    )}
                    {isColumnVisible('membership') && (
                        <div className="flex-center-vertical">
                            <P noDefaultMargin>{getMembershipLabel(memberStatus)}</P>
                        </div>
                    )}
                    {isColumnVisible('role') && (
                        <div className="flex-center-vertical">
                            <P noDefaultMargin>{getMemberRoleLabel(role)}</P>
                        </div>
                    )}
                    <DeviceAlert
                        deviceInfo={deviceInfo}
                        exchangeDeviceInfo={exchangeDeviceInfo}
                        hasMismatch={hasMismatch}
                        mismatchContent={mismatchContent}
                    />
                </>
            </Container>
        </div>
    );
};

export default ListItem;
